import axios from "axios";
import { EventSource } from "eventsource";
import APIURLMaker from "../../utils/APIURLMaker";
import { getAuthorizationHeaders, getBearerToken } from "../authToken";
import { createAuthenticatedEventSourceFetch } from "./authenticatedEventSourceFetch";
import { SSE_EVENTS, SSE_STATUS, safeJsonParse } from "./sseContract";

const DEFAULT_RETRY = {
  initialMs: 1000,
  maxMs: 15000,
};

const getRetryDelay = (attempt, retryConfig) => {
  const baseDelay = retryConfig.initialMs * Math.pow(2, Math.max(0, attempt - 1));
  const cappedDelay = Math.min(baseDelay, retryConfig.maxMs);
  const jitter = Math.floor(Math.random() * 300);
  return cappedDelay + jitter;
};

let suspendedForAuthFailure = false;
const liveStreams = new Set();

/** Stop every open live stream and do not reconnect until the page reloads after sign-in. */
export const suspendSseForAuthFailure = () => {
  suspendedForAuthFailure = true;
  liveStreams.forEach((stop) => stop());
};

export const createSseStream = ({
  userId,
  subject,
  getToken = getBearerToken,
  onPayload,
  onStatus,
  onError,
  retry = DEFAULT_RETRY,
}) => {
  let eventSource = null;
  let closedByClient = false;
  let reconnectTimer = null;
  let reconnectAttempt = 0;

  const subscribeUrl = `${APIURLMaker("sse/subscribe")}?id=${encodeURIComponent(userId)}&subject=${encodeURIComponent(subject)}`;
  const unsubscribeUrl = APIURLMaker("sse/unsubscribe");
  const authenticatedFetch = createAuthenticatedEventSourceFetch(getToken);

  const clearReconnectTimer = () => {
    if (!reconnectTimer) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  };

  const closeEventSource = () => {
    if (!eventSource) return;
    try {
      eventSource.close();
    } catch (error) {
      // no-op
    }
    eventSource = null;
  };

  const unsubscribeOnBackend = async () => {
    try {
      await axios.delete(unsubscribeUrl, {
        params: { id: userId, subject },
        headers: getAuthorizationHeaders(),
      });
    } catch (error) {
      // Best-effort cleanup. Ignore network/backend failures.
    }
  };

  const stopForAuthFailure = () => {
    liveStreams.delete(stopForAuthFailure);
    closedByClient = true;
    clearReconnectTimer();
    closeEventSource();
    onStatus?.(SSE_STATUS.CLOSED);
  };

  liveStreams.add(stopForAuthFailure);

  const connect = () => {
    if (closedByClient || suspendedForAuthFailure) return;

    const token = getToken?.();
    if (!token) {
      reconnectAttempt += 1;
      onStatus?.(SSE_STATUS.RETRYING);
      const delay = getRetryDelay(reconnectAttempt, retry);
      reconnectTimer = setTimeout(connect, delay);
      return;
    }

    onStatus?.(reconnectAttempt === 0 ? SSE_STATUS.CONNECTING : SSE_STATUS.RETRYING);

    eventSource = new EventSource(subscribeUrl, {
      fetch: authenticatedFetch,
    });

    eventSource.onopen = () => {
      reconnectAttempt = 0;
      onStatus?.(SSE_STATUS.OPEN);
    };

    eventSource.addEventListener(subject, (event) => {
      const payload = safeJsonParse(event.data);
      onPayload?.(payload);
    });

    eventSource.addEventListener(SSE_EVENTS.HEARTBEAT, (event) => {
      onStatus?.(SSE_EVENTS.HEARTBEAT, event.data);
    });

    eventSource.onerror = (error) => {
      closeEventSource();

      if (closedByClient || suspendedForAuthFailure) return;

      reconnectAttempt += 1;
      onError?.(error);
      onStatus?.(SSE_STATUS.ERROR);

      const delay = getRetryDelay(reconnectAttempt, retry);
      reconnectTimer = setTimeout(connect, delay);
    };
  };

  connect();

  return async () => {
    liveStreams.delete(stopForAuthFailure);
    closedByClient = true;
    clearReconnectTimer();
    closeEventSource();
    onStatus?.(SSE_STATUS.CLOSED);
    // A dead session cannot unsubscribe cleanly, and that request would be rejected again.
    if (!suspendedForAuthFailure) {
      await unsubscribeOnBackend();
    }
  };
};
